import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Table, TableBody, useMediaQuery, Button, TableCell, TableContainer, TableHead, FormControl, InputLabel, Select, MenuItem, TableRow, IconButton, Card, Typography, CardContent, Stack, Menu, ListItemIcon, Grid, Link } from '@mui/material';
import { Loading } from '../../../components/loading/Loading';
import { UserContext } from '../../../context/user_context/UserContext';
import { customerSnapshotListener, deleteClient } from '../../../repository/ClientDAO';
import AppSidebar from '../../../components/navbar/DesktopAppSidebar';
import EditCustomerModal from './modals/EditCustomerModal';
import AddNoteModal from './modals/AddNoteModal';
import { clientEventSnapshotListener } from '../../../repository/EventDAO';
import { customerNoteSnapshotListener, deleteCustomerNote } from '../../../repository/NotesDAO';
import { useLoadingContext } from '../../../context/loading/LoadingContext';
import { formatFirestoreTimestampMMDDYYYY } from '../../../utils/time_utils/TimeUtils';
import { useToast, ToastType } from '../../../context/toast/ToastContext';
import EventModal from '../Calendar/modals/EventModal';
import AddEventModal from '../Calendar/modals/AddEventModal';
import ConfirmDeleteModal from '../../../components/modals/ConfirmDeleteModal';
import usePermissions from '../../../utils/permissions/UsePermissions';
import { Modules } from '../../../utils/permissions/Modules';
import { PermissionTypes } from '../../../utils/permissions/PermissionTypes';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../resources/strings/StringKeys';
import { CalendarMonth, Delete, Edit, MoreVert, Note, Notes, OpenInNew, NoteAdd } from '@mui/icons-material';
import ViewNoteModal from '../../../components/notes/ViewNoteModal';
import { getCustomerInvoices } from '../../../repository/InvoicesDAO';
import { useGoogleCalendarContext } from '../../../context/google_calendar/GoogleCalendarContext';
import { useFirebaseEvents } from '../Calendar/hooks/useFirebaseEvents';
import { useOrganizationContext } from '../../../context/organization_context/OrganizationContext';
import { handleMenuClick, handleMenuClose } from '../../../components/utils/MenuHandlers';

function CustomerDetail() {

    const { customerId } = useParams();
    const [customerData, setCustomerData] = useState();
    const [noteData, setNoteData] = useState([]);
    const [invoiceData, setInvoiceData] = useState([])
    const [showCustomerToEditModal, setShowCustomerToEditModal] = useState(false);
    const { firebaseUser } = useContext(UserContext);
    const [showAddEventModal, setShowAddEventModal] = useState(false);
    const [showAddNoteModal, setShowAddNoteModal] = useState(false);
    const [eventData, setEventData] = useState([]);
    const { setLoadingData } = useLoadingContext();
    const { showToast } = useToast();
    const [eventFilter, setEventFilter] = useState('upcoming');
    const [filteredEvents, setFilteredEvents] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState();
    const [showEventModal, setShowEventModal] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmNoteOpen, setConfirmNoteOpen] = useState(false);
    const [noteToDelete, setNoteToDelete] = useState(null);
    const [selectedNote, setSelectedNote] = useState(null);
    const [showNoteModal, setShowNoteModal] = useState(false); 
    const { 
        googleAccessToken,
        addToGoogleCalendar,
        isGoogleConnected,
        syncCalendar,
    } = useGoogleCalendarContext();
    const isMobile = useMediaQuery('(max-width:600px)');
    const { hasPermission } = usePermissions();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState(null);
    const { organizationCustomers, organizationEmployees } = useOrganizationContext();
    const [isLoadingInvoices, setIsLoadingInvoices] = useState(true);

    const { events, addEvent, deleteEvent, updateEvent } = useFirebaseEvents(
        firebaseUser,
        organizationEmployees,
        organizationCustomers
    );

    useEffect(() => {
        if (customerId && firebaseUser) {
            const unsubscribe = customerSnapshotListener(firebaseUser.organization, customerId, setCustomerData);
            return () => unsubscribe();
        }
    }, [customerId, firebaseUser]);

    useEffect(() => {
        if (customerId && firebaseUser) {
            const unsubscribe = customerNoteSnapshotListener(firebaseUser.organization, customerId, setNoteData);
            return () => unsubscribe();
        }
    }, [customerId, firebaseUser]);

    useEffect(() => {
        async function fetchInvoices() {
          if (customerId && firebaseUser) {
            try {
              setIsLoadingInvoices(true);
              const data = await getCustomerInvoices(firebaseUser.organization, customerId);
              setInvoiceData(data);
            } catch (error) {
                setInvoiceData([])
            } finally {
                setIsLoadingInvoices(false);
            }
          }
        }
      
        fetchInvoices();
      }, [customerId, firebaseUser]);
      
    useEffect(() => {
        if (customerId && firebaseUser) {
            const unsubscribe = clientEventSnapshotListener(firebaseUser.organization, customerId, setEventData);
            return () => unsubscribe();
        }
    }, [customerId, firebaseUser]);

    useEffect(() => {
        const now = new Date();
        const filtered = eventData.filter(event => {
            const eventDate = new Date(event.start.seconds * 1000);
            return eventFilter === 'all' ? true : eventDate >= now;
        });
        setFilteredEvents(filtered);
    }, [eventData, eventFilter]);

    const deleteNoteItem = async () => {
        if (!selectedNote) return;
        setLoadingData(true);
    
        try {
            const result = await deleteCustomerNote(firebaseUser.organization, customerId, selectedNote.id);
    
            if (result.success) {
                showToast(t(StringKeys.SUCCESSFULLY_DELETED_NOTE), ToastType.SUCCESS);
            } else {
                showToast(t(StringKeys.FAILED_TO_DELETE_NOTE), ToastType.DANGER);
            }
        } catch (error) {
            showToast(t(StringKeys.FAILED_TO_DELETE_NOTE), ToastType.DANGER);
        } finally {
            setLoadingData(false);
            setSelectedNote(null);
        }
    };
    

    const handleCustomerDelete = async () => {
        setLoadingData(true);
    
        try {
            const result = await deleteClient(firebaseUser.organization, customerId);
    
            if (result.success) {
                showToast(t(StringKeys.SUCCESSFULLY_DELETED_X, {x:`${customerData.first_name}`}), ToastType.SUCCESS);
                navigate('/customers', { replace: true });
            } else {
                showToast(t(StringKeys.FAILED_TO_DELETE_X, {x:`${customerData.first_name}`}), ToastType.DANGER);
            }
        } catch (error) {
            showToast(t(StringKeys.FAILED_TO_DELETE_X, {x:`${customerData.first_name}`}), ToastType.DANGER);
        } finally {
            setLoadingData(false);
        }
    };

    const handleNoteClick = (note) => {
        setSelectedNote(note);
        setShowNoteModal(true);
    };

    const handleAddEvent = async (organization, eventData) => {
        try {
            const result = await addEvent(organization, eventData);
            if (result.success) {
                // Wait a bit before syncing to ensure Firebase update is complete
                setTimeout(async () => {
                    await syncCalendar();
                }, 1000);
            }
            return result;
        } catch (error) {
            console.error('Error adding event:', error);
            throw error;
        }
    };

    if (!customerData || !noteData) return <AppSidebar content={<Loading />} />;

    return (
        <>
            <AppSidebar
                content={
                    <>
                        <Stack gap={4}>
                            <Grid container alignItems="center" spacing={2}>
                                {/* Spacer Grid item */}
                                {!isMobile && (
                                    <Grid item xs>
                                        <Stack direction="row" spacing={2}>
                                        {hasPermission(Modules.CUSTOMERS, PermissionTypes.WRITE) && (
                                                <Button
                                                    variant="text"
                                                    startIcon={<Edit />}
                                                    onClick={() => setShowCustomerToEditModal(true)}
                                                    sx={{
                                                        textTransform: 'none',
                                                        fontSize: '1rem',
                                                    }}
                                                >
                                                    {t(StringKeys.EDIT)}
                                                </Button>                                                
                                            )}

                                            {hasPermission(Modules.CUSTOMERS, PermissionTypes.WRITE) && (
                                                <Button
                                                    variant="text"
                                                    startIcon={<NoteAdd />}
                                                    onClick={() => setShowAddNoteModal(true)}
                                                    sx={{
                                                        textTransform: 'none',
                                                        fontSize: '1rem',
                                                    }}
                                                >
                                                    {t(StringKeys.ADD_NOTE)}
                                                </Button>
                                            )}
                                            {hasPermission(Modules.CUSTOMERS, PermissionTypes.WRITE) && (
                                                <Button 
                                                    variant="text" 
                                                    startIcon={<CalendarMonth />} 
                                                    onClick={() => setShowAddEventModal(true)}
                                                    sx={{
                                                        textTransform: 'none',
                                                        fontSize: '1rem',
                                                    }}
                                                >
                                                    {t(StringKeys.ADD_EVENT)}
                                                </Button>
                                            )}
                                            {hasPermission(Modules.CUSTOMERS, PermissionTypes.WRITE) && !isLoadingInvoices && invoiceData.length < 1 && (
                                                <Button 
                                                    variant="text" 
                                                    startIcon={<Delete />} 
                                                    onClick={() => setConfirmOpen(true)} 
                                                    sx={{
                                                        textTransform: 'none',
                                                        fontSize: '1rem',
                                                    }}
                                                >
                                                    {t(StringKeys.DELETE)}
                                                </Button>
                                            )}
                                        </Stack>
                                    </Grid>
                                )}
                                {isMobile && (
                                    <Grid item xs="auto">
                                        <IconButton onClick={(event) => handleMenuClick(event, setAnchorEl)}>
                                            <MoreVert />
                                        </IconButton>
                                        <Menu
                                            anchorEl={anchorEl}
                                            open={Boolean(anchorEl)}
                                            onClose={() => handleMenuClose(setAnchorEl)}
                                        >
                                            {hasPermission(Modules.CUSTOMERS, PermissionTypes.WRITE) && (
                                                <MenuItem onClick={() => { setShowCustomerToEditModal(true); handleMenuClose(setAnchorEl); }}>
                                                    <ListItemIcon>
                                                        <Edit fontSize="small" />
                                                    </ListItemIcon>
                                                    {t(StringKeys.EDIT)}
                                                </MenuItem>
                                            )}                                          
                                            {hasPermission(Modules.CUSTOMERS, PermissionTypes.WRITE) && (
                                                <MenuItem onClick={() => { setShowAddNoteModal(true); handleMenuClose(setAnchorEl); }}>
                                                    <ListItemIcon>
                                                        <NoteAdd fontSize="small" />
                                                    </ListItemIcon>
                                                    {t(StringKeys.ADD_NOTE)}
                                                </MenuItem>
                                            )}
                                            {/* {hasPermission(Modules.CUSTOMERS, PermissionTypes.WRITE_EVENTS) && (
                                                <MenuItem onClick={() => { setShowAddEventModal(true); handleMenuClose(); }}>
                                                    <ListItemIcon>
                                                        <CalendarMonth fontSize="small" />
                                                    </ListItemIcon>
                                                    {t(StringKeys.ADD_EVENT)}
                                                </MenuItem>
                                            )} */}
                                            {hasPermission(Modules.CUSTOMERS, PermissionTypes.WRITE) && !isLoadingInvoices && invoiceData.length < 1 && (
                                                <MenuItem onClick={() => { setConfirmOpen(true); handleMenuClose(setAnchorEl); }}>
                                                    <ListItemIcon>
                                                        <Delete fontSize="small" />
                                                    </ListItemIcon>
                                                    {t(StringKeys.DELETE)}
                                                </MenuItem>
                                            )}                                       
                                        </Menu>
                                    </Grid>
                                )}
                            </Grid>
                            <Card variant="outlined">
                                <CardContent>
                                    <Typography variant={isMobile ? "h6" : "h5"}>{customerData.first_name} {customerData.last_name}</Typography>
                                    <Typography>{t(StringKeys.PHONE)}: {customerData.phone_number}</Typography>
                                    <Typography>{t(StringKeys.EMAIL)}: {customerData.email}</Typography>
                                    <Typography>{t(StringKeys.ADDRESS)}: {(() => {
                                        // Check if any address components exist and are not empty strings
                                        const hasAddress = customerData.address && customerData.address.trim() !== '';
                                        const hasCity = customerData.city && customerData.city.trim() !== '';
                                        const hasState = customerData.state && customerData.state.trim() !== '';
                                        const hasZip = customerData.zip_code && customerData.zip_code.trim() !== '';
                                        
                                        // Build address based on available components
                                        if (hasAddress || hasCity || hasState || hasZip) {
                                            let addressParts = [];
                                            
                                            if (hasAddress) addressParts.push(customerData.address.trim());
                                            
                                            // Combine city and state if both exist
                                            if (hasCity && hasState) {
                                                addressParts.push(`${customerData.city.trim()}, ${customerData.state.trim()}`);
                                            } else {
                                                if (hasCity) addressParts.push(customerData.city.trim());
                                                if (hasState) addressParts.push(customerData.state.trim());
                                            }
                                            
                                            if (hasZip) addressParts.push(customerData.zip_code.trim());
                                            
                                            return addressParts.join(' ');
                                        } else {
                                            return t(StringKeys.UNASSIGNED);
                                        }
                                    })()}</Typography>
                                </CardContent>
                            </Card>
                            {hasPermission(Modules.CUSTOMERS, PermissionTypes.READ_EVENTS) && (
                                <Card variant="outlined">
                                    <CardContent>
                                        <Stack direction="row" spacing={3}>
                                            <Typography variant={isMobile ? "h6" : "h5"} component="div">
                                                {t(StringKeys.EVENTS)}
                                            </Typography>
                                            <FormControl variant="outlined" size="small" style={{ minWidth: 120 }}>
                                                <InputLabel id="event-filter-label">{t(StringKeys.FILTER)}</InputLabel>
                                                <Select
                                                    labelId="event-filter-label"
                                                    id="event-filter-select"
                                                    value={eventFilter}
                                                    onChange={(e) => setEventFilter(e.target.value)}
                                                    label={t(StringKeys.FILTER)}
                                                >
                                                    <MenuItem value="upcoming">{t(StringKeys.UPCOMING_EVENTS)}</MenuItem>
                                                    <MenuItem value="all">{t(StringKeys.ALL_EVENTS)}</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Stack>
                                        {filteredEvents.length > 0 ? (
                                            <TableContainer>
                                                <Table aria-label="events-table">
                                                    <TableBody>
                                                        {filteredEvents.map((event) => (
                                                            <TableRow key={event.id}>
                                                                <TableCell>{formatFirestoreTimestampMMDDYYYY(event.start)}</TableCell>
                                                                <TableCell>{event.title}</TableCell>
                                                                <TableCell>{event.description}</TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        ) : (
                                            <TableContainer>
                                                <Table aria-label="no-events-table">
                                                    <TableBody>
                                                        <TableRow>
                                                            <TableCell colSpan={3} align="center">
                                                                <Typography variant="body2">{t(StringKeys.NO_EVENTS_TO_DISPLAY)}</Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        )}
                                    </CardContent>
                                </Card>
                            )}
                            {hasPermission(Modules.INVOICES, PermissionTypes.READ) && (
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant={isMobile ? "h6" : "h5"} component="div">{t(StringKeys.INVOICES)}</Typography>
                                        {
                                            invoiceData.length > 0 ? (
                                                <TableContainer>
                                                <Table aria-label="invoices-table">
                                                    <TableHead>
                                                    <TableRow>
                                                        <TableCell style={{ fontWeight: 'bold' }}>{t(StringKeys.INVOICE_ID)}</TableCell>
                                                        {!isMobile && <TableCell style={{ fontWeight: 'bold' }}>{t(StringKeys.AMOUNT)}</TableCell>}
                                                        {!isMobile && <TableCell style={{ fontWeight: 'bold' }}>{t(StringKeys.STATUS)}</TableCell>}
                                                        {!isMobile && <TableCell style={{ fontWeight: 'bold' }}>{t(StringKeys.DUE_DATE)}</TableCell>}
                                                        {isMobile && <TableCell></TableCell>}
                                                    </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                    {invoiceData.map((invoice) => (
                                                        <TableRow
                                                        key={invoice.id}
                                                        sx={{ '&:last-child td, &:last-child th': { borderBottom: "unset" } }}
                                                        >
                                                        <TableCell>
                                                            <Link
                                                                onClick={() => {
                                                                    navigate(`/invoices/${invoice.id}`);
                                                                }}
                                                                sx={{ cursor: "pointer" }}
                                                            >
                                                                {invoice.number}
                                                            </Link>
                                                        </TableCell>
                                                        <TableCell>${(invoice.total / 100).toFixed(2)}</TableCell>
                                                        <TableCell>
                                                            {t(invoice.status)}
                                                        </TableCell>
                                                        <TableCell>
                                                            {new Date(invoice.due_date.seconds * 1000).toLocaleDateString()}                                                        
                                                        </TableCell>
                                                        </TableRow>
                                                    ))}
                                                    </TableBody>
                                                </Table>
                                                </TableContainer>
                                            ) : (
                                                <TableContainer>
                                                    <Table aria-label="no-notes-table">
                                                        <TableBody>
                                                            <TableRow>
                                                                <TableCell colSpan={3} align="center">
                                                                    <Typography variant="body2">{t(StringKeys.NO_INVOICES_TO_DISPLAY)}</Typography>
                                                                </TableCell>
                                                            </TableRow>
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            )
                                        }
                                    </CardContent>
                                </Card>
                            )}
                            {hasPermission(Modules.CUSTOMERS, PermissionTypes.READ) && hasPermission(Modules.CUSTOMERS, PermissionTypes.WRITE) && (
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant={isMobile ? "h6" : "h5"} component="div">{t(StringKeys.NOTES)}</Typography>
                                        {noteData.length > 0 ? (
                                            <TableContainer key={"notes-table"}>
                                                <Table>
                                                    <TableBody>
                                                        {noteData.map(note => (
                                                            <TableRow style={{ cursor: "pointer" }}>
                                                                <TableCell>
                                                                    {formatFirestoreTimestampMMDDYYYY(note.date)} - {note.title}
                                                                </TableCell>
                                                                {hasPermission(Modules.CUSTOMERS, PermissionTypes.WRITE) && (                                                            
                                                                    <>
                                                                        <TableCell align="right">
                                                                            <Button
                                                                                variant="text"
                                                                                onClick={() => handleNoteClick(note)}
                                                                            >
                                                                                <OpenInNew />
                                                                            </Button>
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <Button
                                                                                variant="text"
                                                                                onClick={() => {
                                                                                    setSelectedNote(note);
                                                                                    setConfirmNoteOpen(true);
                                                                                }}
                                                                            >
                                                                                <Delete />
                                                                            </Button>
                                                                        </TableCell>
                                                                    </>
                                                                )}
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        ) : (
                                            <TableContainer>
                                                <Table aria-label="no-notes-table">
                                                    <TableBody>
                                                        <TableRow>
                                                            <TableCell colSpan={3} align="center">
                                                                <Typography variant="body2">{t(StringKeys.NO_NOTES_TO_DISPLAY)}</Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        )}
                                    </CardContent>
                                </Card>
                            )}

                            <EditCustomerModal
                                show={showCustomerToEditModal}
                                onHide={() => setShowCustomerToEditModal(false)}
                                client={customerData}
                            />
                            <AddEventModal
                                show={showAddEventModal}
                                onHide={() => setShowAddEventModal(false)}
                                preselectedCustomer={customerData}
                                googleAccessToken={googleAccessToken}
                                isGoogleConnected={isGoogleConnected}
                                onAdd={handleAddEvent}
                                onAddToGoogle={addToGoogleCalendar}
                            />
                            <AddNoteModal
                                show={showAddNoteModal}
                                onHide={() => setShowAddNoteModal(false)}
                                client={customerData}
                            />
                            <EventModal
                                show={showEventModal}
                                onHide={() => {
                                    setSelectedEvent();
                                    setShowEventModal(false);
                                }}
                                event={selectedEvent}
                            />
                            <ViewNoteModal
                                open={showNoteModal}
                                onClose={() => setShowNoteModal(false)} 
                                note={selectedNote}
                                onDelete={deleteNoteItem}
                            />
                            <ConfirmDeleteModal
                                open={confirmOpen}
                                onClose={() => setConfirmOpen(false)}
                                onConfirm={() => { handleCustomerDelete(); setConfirmOpen(false); }}
                                item={`${customerData.first_name} ${customerData.last_name}`}
                            />
                            <ConfirmDeleteModal
                                open={confirmNoteOpen}
                                onClose={() => setConfirmNoteOpen(false)}
                                onConfirm={() => { deleteNoteItem(); setConfirmNoteOpen(false); }}
                                item="note"
                            />
                        </Stack>
                    </>
                }
            />
        </>
    );
}

export default CustomerDetail;
